import React, { useCallback, useMemo, useRef, useState } from "react";
import { ColorPicker } from "antd";
import { useRecentColors } from '../../hooks/useRecentColors';
import { useEyeDropper } from '../../hooks/useEyeDropper';
import { createColorPickerPanel, EyeDropperIcon } from '../../utils/colorPickerPanel';
import { transformColor } from "../../../core/utils/generalUtils";
import { typography, textColors, surfaces, borders } from "../../../design-system/tokens";
import { useExpandedField } from "../../context/ExpandedFieldContext";



export const ColorField = ({ value, onChange, label, help, field, nullable, nullLabel }) => {
  // Use shared expanded field context for accordion behavior
  // Create a unique field ID - use the field prop if provided, otherwise generate one
  const fieldId = field ? `color-field-${field}` : `color-field-${label || 'default'}`;
  let isExpandedContext, setExpandedContext;
  try {
    const context = useExpandedField();
    isExpandedContext = context.isExpanded;
    setExpandedContext = context.setExpanded;
  } catch {
    // Context not available, use local state
    isExpandedContext = null;
    setExpandedContext = null;
  }
  const [localIsOpen, setLocalIsOpen] = useState(false);
  // Use context value if available, otherwise fall back to local state
  const isOpen = isExpandedContext ? isExpandedContext(fieldId) : localIsOpen;
  const lastSentColorRef = useRef(null);
  const { 
    recentColors,
    setPendingColor, 
    addPendingColor,
    addColorImmediately,
    currentPageColors, 
    currentPage, 
    totalPages, 
    goToNextPage, 
    goToPrevPage 
  } = useRecentColors();
  const { isSupported: isEyeDropperSupported, isActive: isEyeDropperActive, pickColor } = useEyeDropper();

  const handleColorChange = useCallback((color) => {
    // onChange is expecting a hex string
    const hexColor = color?.toHexString?.() || null;
    if (!hexColor) return;
    // Avoid redundant updates (drag events can fire very frequently)
    if (lastSentColorRef.current === hexColor) return;
    lastSentColorRef.current = hexColor;
    onChange(hexColor);
    // Set pending color to be added when picker closes
    setPendingColor(hexColor);
  }, [onChange, setPendingColor]);

  const handleColorSelect = useCallback((hex) => {
    if (!hex) return;
    if (lastSentColorRef.current === hex) {
      // Close picker
      if (setExpandedContext) {
        setExpandedContext(null);
      } else {
        setLocalIsOpen(false);
      }
      return;
    }
    lastSentColorRef.current = hex;
    onChange(hex);
    setPendingColor(hex); // Set pending color
    // Close the picker after selection
    if (setExpandedContext) {
      setExpandedContext(null);
    } else {
      setLocalIsOpen(false);
    }
  }, [onChange, setPendingColor, setExpandedContext]);

  const normalizedValue = useMemo(() => {
    if (!value) return "#ffffff";
    if (typeof value === "string" && value.startsWith("#")) return value;
    try {
      return transformColor(value, 100, "hex");
    } catch (_) {
      return "#ffffff";
    }
  }, [value]);

  const handleEyeDropperPick = useCallback(async () => {
    // Close the color picker popup so it doesn't overlay the sampling area
    if (setExpandedContext) {
      setExpandedContext(null);
    } else {
      setLocalIsOpen(false);
    }
    // Let the popup close animation finish before entering eyedropper mode
    await new Promise(resolve => setTimeout(resolve, 150));

    const hex = await pickColor();
    if (hex) {
      lastSentColorRef.current = hex;
      onChange(hex);
      addColorImmediately(hex);
    }
  }, [pickColor, onChange, addColorImmediately, setExpandedContext]);

  const latestRef = useRef(null);
  latestRef.current = {
    normalizedValue,
    handleColorSelect,
    recentColors,
    currentPageColors,
    currentPage,
    totalPages,
    goToNextPage,
    goToPrevPage,
    addColorImmediately,
    handleEyeDropperPick,
    isEyeDropperSupported,
  };
  const panelRender = useCallback((panel) => {
    const latest = latestRef.current;
    if (!latest) return panel;
    return createColorPickerPanel({
      currentValue: latest.normalizedValue,
      onColorSelect: latest.handleColorSelect,
      recentColors: latest.recentColors,
      currentPageColors: latest.currentPageColors,
      currentPage: latest.currentPage,
      totalPages: latest.totalPages,
      goToNextPage: latest.goToNextPage,
      goToPrevPage: latest.goToPrevPage,
      addColorImmediately: latest.addColorImmediately,
      onEyeDropperPick: latest.handleEyeDropperPick,
      isEyeDropperSupported: latest.isEyeDropperSupported,
    })(panel);
  }, []);

  const handleClearColor = useCallback(() => {
    lastSentColorRef.current = null;
    onChange(null);
  }, [onChange]);

  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <div className="field-wrapper">
      {label && <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>{label}</label>}
      <div className="flex items-center gap-3">
        <ColorPicker
          value={normalizedValue}
          onChange={handleColorChange}
          panelRender={panelRender}
          open={isOpen}
          onOpenChange={(open) => {
            if (setExpandedContext) {
              if (open) {
                setExpandedContext(fieldId);
              } else {
                if (isExpandedContext && isExpandedContext(fieldId)) {
                  setExpandedContext(null);
                }
              }
            } else {
              setLocalIsOpen(open);
            }
            if (!open) {
              addPendingColor();
            }
          }}
        />
        {isEyeDropperSupported && (
          <button
            type="button"
            onClick={handleEyeDropperPick}
            disabled={isEyeDropperActive}
            className={`inline-flex items-center justify-center w-7 h-7 rounded-md border ${borders.default} ${textColors.tertiary} hover:text-blue-500 dark:hover:text-blue-400 ${surfaces.hover} transition-colors ${
              isEyeDropperActive
                ? 'ring-2 ring-blue-300 dark:ring-blue-700 border-blue-400 dark:border-blue-500'
                : 'cursor-pointer'
            }`}
            title="Pick color from screen"
            aria-label="Pick color from screen"
          >
            <EyeDropperIcon size={14} />
          </button>
        )}
        {nullable && (
          nullLabel ? (
            <button
              type="button"
              onClick={handleClearColor}
              disabled={!hasValue}
              className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md border ${borders.default} ${typography.caption} font-medium transition-colors ${hasValue ? `${surfaces.hover} ${textColors.secondary} cursor-pointer` : `opacity-40 ${textColors.muted} cursor-default`}`}
              title={hasValue ? nullLabel : `Already ${nullLabel.toLowerCase()}`}
              aria-label={nullLabel}
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="5.7" y1="5.7" x2="18.3" y2="18.3" />
              </svg>
              {nullLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleClearColor}
              disabled={!hasValue}
              className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${borders.default} transition-colors ${hasValue ? `${surfaces.hover} cursor-pointer` : 'opacity-40 cursor-default'}`}
              title={hasValue ? "Remove color" : "No color set"}
              aria-label="Remove color"
            >
              <svg
                className={`w-4 h-4 ${textColors.muted}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="5.7" y1="5.7" x2="18.3" y2="18.3" />
              </svg>
            </button>
          )
        )}
      </div>
      {help && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
};
